import React, { useState, FormEvent, useEffect, useRef, useCallback } from 'react';
import { format, differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import './App.css';

const WEDDING_DATE = new Date('2025-08-09T11:30:00');
const STORAGE_KEY = 'wedding_rsvps';
const VENUE_NAME = "Sime Darby Convention Centre";
// Use a relative path for proxying via React dev server (see package.json "proxy" field)
const YOUR_GOOGLE_APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbzRQbm7RBfLHktUOzJJgOD5gabG0NH_DqDKy01DdINoQPl9cyHOZq0HEu8VIh5_R6BE/exec";



declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

interface QuickActionsProps {
  isPlaying: boolean;
  toggleMusic: () => void;
  scrollToWishes: () => void;
}

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 1.2 } }
};

const slideUp = {
  hidden: { y: 50, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
};

const staggerChildren = {
  visible: {
    transition: {
      staggerChildren: 0.3
    }
  }
};

const CONTACTS = [
  { name: "En Mizwan", phone: "+60197000756", role: "Wedding Guest Support" },
];

// Add dropdown options
const ORGANIZATION_OPTIONS = [
  "Family & Relatives",
  "Friends",
  "IJN", 
  "KPJ (Healthcare Group)",
  "UPM",
  "University of Malaya",
  "Others"
];

const INVITATION_FROM_OPTIONS = [
  "Dato' Sri",
  "Datin Sri",
  "Esyraq (Groom)",
  "Yasmin (Bride)",
  "Erzan",
  "Farzana"
];

// Add these interfaces near the top where other interfaces are defined
interface Message {
  name: string;
  message: string;
  timestamp: string;
  pax?: number;
  phone?: string;
  organization?: string;
  invitationFrom?: string;
}

const QuickActions: React.FC<QuickActionsProps> = ({ 
  isPlaying, 
  toggleMusic, 
  scrollToWishes
}) => {
  const [showMapOptions, setShowMapOptions] = useState(false);
  const [showContacts, setShowContacts] = useState(false);

  const openGoogleMaps = () => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(VENUE_NAME)}`);
    setShowMapOptions(false);
  };

  const openWaze = () => {
    window.open(`https://waze.com/ul?q=${encodeURIComponent(VENUE_NAME)}&navigate=yes`);
    setShowMapOptions(false);
  };

  const handleMapOptionClick = (action: () => void) => {
    action();
    setShowMapOptions(false);
  };

  const handleCall = (phoneNumber: string) => {
    window.location.href = `tel:${phoneNumber}`;
    setShowContacts(false);
  };

  const closeAllSliders = () => {
    setShowMapOptions(false);
    setShowContacts(false);
  };

  const toggleContacts = () => {
    if (showContacts) {
      setShowContacts(false);
    } else {
      closeAllSliders();
      setShowContacts(true);
    }
  };

  const toggleMapOptions = () => {
    if (showMapOptions) {
      setShowMapOptions(false);
    } else {
      closeAllSliders();
      setShowMapOptions(true);
    }
  };

  return (
    <>
      <AnimatePresence>
        {showContacts && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
            onClick={() => setShowContacts(false)}
          >
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 20, stiffness: 200 }}
              className="bg-gradient-to-b from-[#FEFEFE] to-[#f0f0f0] rounded-t-xl p-4 mb-20 w-full max-w-[430px] space-y-4 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-serif text-[#C27F89]">Contact Us</h3>
                <button 
                  onClick={() => setShowContacts(false)}
                  className="p-2 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-3">
                {CONTACTS.map((contact, index) => (
                  <button
                    key={index}
                    onClick={() => handleCall(contact.phone)}
                    className="w-full bg-white rounded-lg p-4 shadow-sm hover:shadow-md hover:bg-gray-50 active:bg-gray-100 transition-all duration-200"
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex-1 text-left">
                        <p className="text-lg font-medium text-[#C27F89] mb-0.5">{contact.name}</p>
                        <p className="text-sm text-gray-500">{contact.role}</p>
                      </div>
                      <div className="bg-[#C27F89] text-white p-2.5 rounded-full ml-4 flex-shrink-0 shadow-sm">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}

        {showMapOptions && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
            onClick={() => setShowMapOptions(false)}
          >
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 20, stiffness: 200 }}
              className="bg-gradient-to-b from-[#FEFEFE] to-[#f0f0f0] rounded-t-xl p-4 mb-20 w-full max-w-[430px] space-y-4 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={() => handleMapOptionClick(openGoogleMaps)}
                className="w-full py-4 text-[#C27F89] font-medium rounded-lg bg-gradient-to-b from-[#FEFEFE] to-[#e0e0e0] border-2 border-[#C27F89] shadow-[inset_0_-2px_4px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.1)] active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] active:transform active:translate-y-[1px] transition-all duration-200 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="flex items-center justify-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 367"><path fill="#34A853" d="M70.585 271.865a370.712 370.712 0 0 1 28.911 42.642c7.374 13.982 10.448 23.463 15.837 40.31c3.305 9.308 6.292 12.086 12.714 12.086c6.998 0 10.173-4.726 12.626-12.035c5.094-15.91 9.091-28.052 15.397-39.525c12.374-22.15 27.75-41.833 42.858-60.75c4.09-5.354 30.534-36.545 42.439-61.156c0 0 14.632-27.035 14.632-64.792c0-35.318-14.43-59.813-14.43-59.813l-41.545 11.126l-25.23 66.451l-6.242 9.163l-1.248 1.66l-1.66 2.078l-2.914 3.319l-4.164 4.163l-22.467 18.304l-56.17 32.432l-9.344 54.337Z"/><path fill="#FBBC04" d="M12.612 188.892c13.709 31.313 40.145 58.839 58.031 82.995l95.001-112.534s-13.384 17.504-37.662 17.504c-27.043 0-48.89-21.595-48.89-48.825c0-18.673 11.234-31.501 11.234-31.501l-64.489 17.28l-13.225 75.08Z"/><path fill="#4285F4" d="M166.705 5.787c31.552 10.173 58.558 31.53 74.893 63.023l-75.925 90.478s11.234-13.06 11.234-31.617c0-27.864-23.463-48.68-48.81-48.68c-23.969 0-37.735 17.475-37.735 17.475v-57l76.343-33.68Z"/><path fill="#1A73E8" d="M30.015 45.765C48.86 23.218 82.02 0 127.736 0c22.18 0 38.89 5.823 38.89 5.823L90.29 96.516H36.205l-6.19-50.75Z"/><path fill="#EA4335" d="M12.612 188.892S0 164.194 0 128.414c0-33.817 13.146-63.377 30.015-82.649l60.318 50.759l-77.721 92.368Z"/></svg>
                  <span>Google Maps</span>
                </div>
              </button>
              <button
                onClick={() => handleMapOptionClick(openWaze)}
                className="w-full py-4 text-[#C27F89] font-medium rounded-lg bg-gradient-to-b from-[#FEFEFE] to-[#e0e0e0] border-2 border-[#C27F89] shadow-[inset_0_-2px_4px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.1)] active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)] active:transform active:translate-y-[1px] transition-all duration-200 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <div className="flex items-center justify-center space-x-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currenColour" d="M13.218 0C9.915 0 6.835 1.49 4.723 4.148c-1.515 1.913-2.31 4.272-2.31 6.706v1.739c0 .894-.62 1.738-1.862 1.813c-.298.025-.547.224-.547.522c-.05.82.82 2.31 2.012 3.502c.82.844 1.788 1.515 2.832 2.036a3 3 0 0 0 2.955 3.528a2.966 2.966 0 0 0 2.931-2.385h2.509c.323 1.689 2.086 2.856 3.974 2.21c1.64-.546 2.36-2.409 1.763-3.924a12.84 12.84 0 0 0 1.838-1.465a10.73 10.73 0 0 0 3.18-7.65c0-2.882-1.118-5.589-3.155-7.625A10.899 10.899 0 0 0 13.218 0zm0 1.217c2.558 0 4.967.994 6.78 2.807a9.525 9.525 0 0 1 2.807 6.78A9.526 9.526 0 0 1 20 17.585a9.647 9.647 0 0 1-6.78 2.807h-2.46a3.008 3.008 0 0 0-2.93-2.41a3.03 3.03 0 0 0-2.534 1.367v.024a8.945 8.945 0 0 1-2.41-1.788c-.844-.844-1.316-1.614-1.515-2.11a2.858 2.858 0 0 0 1.441-.846a2.959 2.959 0 0 0 .795-2.036v-1.789c0-2.11.696-4.197 2.012-5.861c1.863-2.385 4.62-3.726 7.6-3.726zm-2.41 5.986a1.192 1.192 0 0 0-1.191 1.192a1.192 1.192 0 0 0 1.192 1.193A1.192 1.192 0 0 0 12 8.395a1.192 1.192 0 0 0-1.192-1.192zm7.204 0a1.192 1.192 0 0 0-1.192 1.192a1.192 1.192 0 0 0 1.192 1.193a1.192 1.192 0 0 0 1.192-1.193a1.192 1.192 0 0 0-1.192-1.192zm-7.377 4.769a.596.596 0 0 0-.546.845a4.813 4.813 0 0 0 4.346 2.757a4.77 4.77 0 0 0 4.347-2.757a.596.596 0 0 0-.547-.845h-.025a.561.561 0 0 0-.521.348a3.59 3.59 0 0 1-3.254 2.061a3.591 3.591 0 0 1-3.254-2.061a.64.64 0 0 0-.546-.348z"/></svg>
                  <span>Waze</span>
                </div>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div 
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ 
          type: "spring",
          damping: 20,
          stiffness: 200,
          delay: 0.2
        }}
        className="fixed bottom-0 left-0 right-0 bg-[#C27F89]/95 text-white py-4 px-6 shadow-[0_-1px_8px_rgba(0,0,0,0.2)] z-[100] max-w-[430px] mx-auto backdrop-blur-sm"
      >
        <div className="flex justify-between items-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleContacts}
            className="flex flex-col items-center space-y-1.5 w-16 touch-manipulation group transition-transform"
          >
            <svg className="w-6 h-6 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <span className="text-xs font-medium opacity-90">Contact</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleMapOptions}
            className="flex flex-col items-center space-y-1.5 w-16 touch-manipulation group transition-transform"
          >
            <svg className="w-6 h-6 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-xs font-medium opacity-90">Location</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToWishes}
            className="flex flex-col items-center space-y-1.5 w-16 touch-manipulation group transition-transform"
          >
            <svg className="w-6 h-6 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <span className="text-xs font-medium opacity-90">Wishes</span>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleMusic}
            className="flex flex-col items-center space-y-1.5 w-16 touch-manipulation group transition-transform"
            aria-label={isPlaying ? 'Pause Music' : 'Play Music'}
          >
            <svg className="w-6 h-6 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isPlaying ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              )}
            </svg>
            <span className="text-xs font-medium opacity-90">{isPlaying ? 'Pause' : 'Play'}</span>
          </motion.button>
        </div>
      </motion.div>
    </>
  );
};

const App: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const [showContent, setShowContent] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);

  const [wishesData, setWishesData] = useState({
    name: '',
    message: '',
    timestamp: '',
    pax: 1,
    phone: '',
    organization: '',
    invitationFrom: ''
  });

  const videoRef = React.useRef<HTMLVideoElement>(null);
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const wishesSectionRef = React.useRef<HTMLElement>(null);
  const loopVideoRef = React.useRef<HTMLVideoElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Add ref for content container
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const autoScrollingRef = React.useRef(false);
  let scrollTimeout: NodeJS.Timeout;

  // Add new state for messages
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentMessagePage, setCurrentMessagePage] = useState(0);
  const messagesPerPage = 5;

  // Add polling interval state and ref
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const isSubmittingRef = useRef(false);

  // Effect to handle video end and transition
  useEffect(() => {
    if (videoEnded) {
      // Start playing the loop video before showing it
      if (loopVideoRef.current) {
        loopVideoRef.current.play();
      }
      // Show content immediately after loop video starts
      setTimeout(() => {
        setShowContent(true);
      }, 100);

      // Start auto-scrolling 5 seconds after content is shown
      setTimeout(() => {
        setIsAutoScrolling(true);
        autoScrollingRef.current = true;
      }, 500);
    }
  }, [videoEnded]);

  // Effect for countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setTimeLeft({
        days: differenceInDays(WEDDING_DATE, now),
        hours: differenceInHours(WEDDING_DATE, now) % 24,
        minutes: differenceInMinutes(WEDDING_DATE, now) % 60,
        seconds: differenceInSeconds(WEDDING_DATE, now) % 60
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Effect to handle auto-scroll
  useEffect(() => {
    if (showContent && contentRef.current && isAutoScrolling) {
      const scrollInterval = setInterval(() => {
        if (autoScrollingRef.current) {
          const scrollPosition = window.scrollY;
          const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
          
          if (scrollPosition < totalHeight) {
            window.scrollBy({
              top: 1,
              behavior: 'auto'
            });
          } else {
            clearInterval(scrollInterval);
            setIsAutoScrolling(false);
            autoScrollingRef.current = false;
          }
        }
      }, 25); // Increased frequency for smoother scrolling

      // Handle user scroll
      const handleUserScroll = () => {
        if (autoScrollingRef.current) {
          autoScrollingRef.current = false;
          setIsAutoScrolling(false);
          clearInterval(scrollInterval);

          // Clear existing timeout
          if (scrollTimeout) {
            clearTimeout(scrollTimeout);
          }

          // Set a new timeout to resume auto-scroll after 3 seconds of no scrolling
          scrollTimeout = setTimeout(() => {
            autoScrollingRef.current = true;
            setIsAutoScrolling(true);
          }, 3000);
        }
      };

      window.addEventListener('wheel', handleUserScroll, { passive: true });
      window.addEventListener('touchmove', handleUserScroll, { passive: true });
      window.addEventListener('keydown', handleUserScroll, { passive: true });

      return () => {
        clearInterval(scrollInterval);
        clearTimeout(scrollTimeout);
        window.removeEventListener('wheel', handleUserScroll);
        window.removeEventListener('touchmove', handleUserScroll);
        window.removeEventListener('keydown', handleUserScroll);
      };
    }
  }, [showContent, isAutoScrolling]);

  // Initialize audio on mount
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause(); // Ensure audio is paused initially
      setIsPlaying(false);
    }
  }, []);

  const handleOpenInvitation = () => {
    setOverlayOpen(true);
    if (audioRef.current) {
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(error => console.error('Error playing audio:', error));
    }
    if (videoRef.current) {
      videoRef.current.play()
        .then(() => {
          // Show QuickActions after 5 seconds
          setTimeout(() => {
            setShowQuickActions(true);
          }, 5000);
        })
        .catch(error => console.error('Error playing video:', error));
    }
  };

  // Toggle music function
  const toggleMusic = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(error => console.error('Error playing audio:', error));
      }
    }
  };

  // Move fetchMessages to a standalone function
  const fetchMessages = useCallback(async () => {
    try {
      const response = await fetch(YOUR_GOOGLE_APPS_SCRIPT_URL);
      if (response.ok) {
        const data = await response.json();
        // Filter out messages with empty message field
        const filteredMessages = (data.messages || []).filter(msg => msg.message && msg.message.trim() !== '');
        setMessages(filteredMessages);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, []);

  // Effect for initial messages fetch and polling setup
  useEffect(() => {
    // Initial fetch
    fetchMessages();

    // Set up polling every 10 seconds
    const interval = setInterval(fetchMessages, 10000);
    setPollingInterval(interval);

    // Cleanup
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [fetchMessages]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    isSubmittingRef.current = true;

    const newRsvpEntry = {
      ...wishesData,
      timestamp: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      action: 'rsvp',
      pax: wishesData.pax || 1, // Ensure pax is at least 1 for RSVP
      organization: wishesData.organization || '',
      invitationFrom: wishesData.invitationFrom || ''
    };

    try {
      // Send to Google Sheets
      await fetch(
        YOUR_GOOGLE_APPS_SCRIPT_URL,
        {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newRsvpEntry)
        }
      );

      // Also save to localStorage as backup
      const existingEntries = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const updatedEntries = [...existingEntries, newRsvpEntry];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedEntries));

      alert('RSVP successfully submitted!');
      
      // Reset form
      setWishesData({
        name: '',
        message: '',
        timestamp: '',
        pax: 1,
        phone: '',
        organization: '',
        invitationFrom: ''
      });
    } catch (error) {
      console.error('Error saving RSVP:', error);
      setSubmitError('Sorry, there was a problem submitting your RSVP. Please try again.');
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  // Add this function to handle pagination
  const handleNextMessages = () => {
    const maxPage = Math.floor((messages.length - 1) / messagesPerPage);
    if (currentMessagePage < maxPage) {
      setCurrentMessagePage(currentMessagePage + 1);
    }
  };

  const handlePrevMessages = () => {
    if (currentMessagePage > 0) {
      setCurrentMessagePage(currentMessagePage - 1);
    }
  };

  const scrollToWishes = () => {
    wishesSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const [activeTab, setActiveTab] = useState('wishes');

  const handleWishesSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    isSubmittingRef.current = true;

    const newWishEntry = {
      ...wishesData,
      timestamp: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      action: 'wishes',
      pax: 0, // Set pax to 0 to indicate it's a wish, not an RSVP
      organization: wishesData.organization || '',
      invitationFrom: wishesData.invitationFrom || ''
    };

    try {
      // Send to Google Sheets
      await fetch(
        YOUR_GOOGLE_APPS_SCRIPT_URL,
        {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newWishEntry)
        }
      );

      // Also save to localStorage as backup
      const existingEntries = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const updatedEntries = [...existingEntries, newWishEntry];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedEntries));

      // Immediately fetch new messages to display the new wish
      await fetchMessages();
      
      alert('Your wishes have been sent!');
      
      // Reset form
      setWishesData({
        name: '',
        message: '',
        timestamp: '',
        pax: 1, // Reset to default for RSVP tab
        phone: '',
        organization: '',
        invitationFrom: ''
      });
    } catch (error) {
      console.error('Error saving wishes:', error);
      setSubmitError('Sorry, there was a problem sending your wishes. Please try again.');
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  // Add a function to handle tab switching
  const handleTabChange = (tab: string) => {
    // Reset any submission errors when switching tabs
    setSubmitError(null);
    
    // If we're in the middle of a submission, don't allow tab switching
    if (isSubmitting) return;
    
    setActiveTab(tab);
  };

  return (
    <div className="min-h-screen bg-[#FEFEFE]">
      <AnimatePresence>
        {!overlayOpen && (
          <motion.div 
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[10001] bg-white flex flex-col items-center justify-between p-0 h-[100dvh] w-full"
          >

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
              className="flex flex-col items-center justify-center flex-grow w-full px-8"
            >
              <img
                src="/images/opening-card.png"
                alt="Opening Card"
                className="max-w-full h-auto mx-auto self-center"
                style={{ maxHeight: '60vh', objectFit: 'contain' }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="w-full px-8 pb-16"
            >
              <button
                onClick={handleOpenInvitation}
                className="w-full bg-[#C27F89] hover:bg-[#d8c8b7] text-[#FFF] font-roboto font-bold px-12 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95 tracking-[0.2em] uppercase"
              >
                OPEN INVITATION
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Background Music */}
      <audio 
        ref={audioRef}
        loop
        src="/audio/wedding-song.mp3"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      <div className="w-full max-w-[430px] mx-auto relative">
        {/* Fixed position container for videos */}
        <div className="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] h-screen z-10 pointer-events-none">
          <AnimatePresence mode="wait">
            {!showContent ? (
              <motion.div 
                key="intro"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
                className="absolute inset-0"
              >
                <video 
                  ref={videoRef}
                  className="w-full h-full object-cover"
                  playsInline
                  muted
                  onEnded={() => setVideoEnded(true)}
                >
                  <source src="/videos/opening.mp4" type="video/mp4" />
                </video>
              </motion.div>
            ) : (
              <motion.div 
                key="loop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="absolute inset-0"
              >
                <video 
                  ref={loopVideoRef}
                  className="w-full h-full object-cover"
                  autoPlay 
                  playsInline
                  muted
                  loop
                >
                  <source src="/videos/opening.mp4" type="video/mp4" />
                </video>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Content */}
        <motion.div 
          ref={contentRef}
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          transition={{ duration: 1, delay: 0.5 }}
          className="relative z-20"
          style={{ marginTop: '100vh' }}
        >
          {/* Parents Section */}
          <motion.section 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="py-16 text-center bg-[#FEFEFE] px-4 relative"
          >
            
            
            {/* Decorative elements */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              className="absolute top-0 left-0 w-24 h-24 border-l-2 border-t-2 border-[#C27F89] opacity-30" 
            />
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
              className="absolute bottom-0 right-0 w-24 h-24 border-r-2 border-b-2 border-[#C27F89] opacity-30" 
            />
            
            {/* Intro Section */}
            <motion.div 
              variants={staggerChildren}
              className="space-y-8 relative"
            >
              {/* Greeting */}
              <motion.div variants={slideUp} className="space-y-3">
                <motion.p 
                  variants={slideUp}
                  className="font-playfair text-lg text-[#846D4E] mb-6"
                >
                  Assalamu'alaikum and Greetings
                </motion.p>
                <motion.div variants={slideUp} className="space-y-1">
                  <motion.p variants={slideUp} className="font-optima text-md uppercase">Dato' Sri Dr. Mohamed Ezani Bin Md Taib</motion.p>
                  <motion.p variants={slideUp} className="font-optima text-md uppercase">&</motion.p>
                  <motion.p variants={slideUp} className="font-optima text-md uppercase">Datin Sri Dr. Rayslida Binti Mohd Rashdan</motion.p>
                </motion.div>
              </motion.div>

              {/* Invitation Text */}
              <motion.div variants={slideUp} className="space-y-4">
                <motion.div variants={slideUp} className="w-12 h-[1px] bg-[#62734B] mx-auto"></motion.div>
                <motion.p variants={slideUp} className="font-optima tracking-[0.1em] text-sm text-[#846D4E]">would like to invite</motion.p>
                <motion.p variants={slideUp} className="font-optima text-base text-[#333] font-medium">Tan Sri - Puan Sri - Dato' Seri - Datin Seri - Dato' - Datin - Encik - Puan - Cik</motion.p>
                <motion.p variants={slideUp} className="font-optima tracking-[0.1em] text-sm text-[#846D4E]">to the wedding reception of our beloved son</motion.p>
                <motion.div variants={slideUp} className="w-12 h-[1px] bg-[#62734B] mx-auto"></motion.div>
              </motion.div>

              {/* Couple Names */}
              <motion.div variants={slideUp} className="space-y-1">
                <motion.p variants={slideUp} className="font-[Carly] text-3xl font-semibold text-[#333]">Mohamed Esyraq</motion.p>
                <motion.p variants={slideUp} className="font-optima text-[#333] text-base">&</motion.p>
                <motion.p variants={slideUp} className="font-[Carly] text-3xl font-semibold text-[#333]">Yasmin Nadiah</motion.p>
              </motion.div>
            </motion.div>
          </motion.section>


          {/* Details Section */}
          <motion.section 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="py-12 bg-[#FEFEFE] relative overflow-hidden"
          >            
            
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-24 h-24 border-l-2 border-t-2 border-[#C27F89] opacity-30" />
            <div className="absolute bottom-0 right-0 w-24 h-24 border-r-2 border-b-2 border-[#C27F89] opacity-30" />
            
            <div className="relative z-10 px-4 space-y-12">
              <motion.div variants={slideUp} className="space-y-3">
                <h3 className="font-optima text-sm uppercase mb-2 text-center font-medium text-[#846D4E]">Date & Time</h3>
                <div className="text-center space-y-1">
                  <p className="font-optima text-base font-medium uppercase tracking-wide">Saturday • August 9th, 2025</p>
                  <p className="font-optima text-base font-medium uppercase tracking-wide">12.00 PM to 4.00 PM</p>
                </div>
              </motion.div>
              
              <motion.div variants={slideUp} className="space-y-3">
                <p className="w-12 text-center uppercase font-optima font-medium text-sm text-[#846D4E] mx-auto">at</p>
                <h3 className="font-optima text-base font-medium uppercase text-center tracking-wide">SIME DARBY CONVENTION CENTRE</h3>
                <p className="font-optima text-sm text-center">1A, Jalan Bukit Kiara 1, Bukit Kiara, 60000 Kuala Lumpur,<br />Wilayah Persekutuan Kuala Lumpur</p>
              </motion.div>

              <div className="flex justify-center">
                <img 
                  src="/images/separator.png" 
                  alt="Decorative separator" 
                  className="h-4 w-auto opacity-80"
                />
              </div>

              {/* Dress Code Section (centered, not edge-to-edge) */}
              <div className="space-y-3 text-center">
                <h3 className="font-optima text-sm uppercase mb-2 font-medium text-[#846D4E]">Dress Code</h3>
                <div className="space-y-1">
                  <p className="font-optima text-base tracking-wide">Gentlemen: Smart casual with khakis</p>
                  <p className="font-optima text-base tracking-wide">Ladies: Floral</p>
                </div>
                <div className="flex justify-center my-6">
                  <img
                    src="/images/dresscode.png"
                    alt="Dress Code"
                    className="max-w-xs w-full h-auto"
                    style={{ display: 'block', margin: '0 auto' }}
                  />
                </div>
              </div>

              {/* Theme Section */}
              <div className="space-y-2 text-center my-8">
                <h3 className="font-optima text-sm uppercase mb-2 font-medium text-[#846D4E]">Theme</h3>
                <p className="font-optima text-base tracking-wide">Oriental Garden</p>
              </div>

              <div className="flex justify-center">
                <img 
                  src="/images/separator.png" 
                  alt="Decorative separator" 
                  className="h-4 w-auto opacity-80"
                />
              </div>

              <motion.div variants={slideUp} className="space-y-3">
                <h3 className="font-optima text-sm uppercase mb-2 text-center font-medium text-[#846D4E]">Programme</h3>
                <p className="font-optima text-base text-center tracking-wide">
                  12.00pm - Arrival of Guests<br /><br />
                  12.45pm - Grand Entrance of Bride & Groom<br /><br />
                  4.00pm - End of Ceremony
                </p>
              </motion.div>
            </div>
          </motion.section>


          {/* Messages Section */}
          <motion.section 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={slideUp}
            className="py-16 bg-[#FEFEFE] relative overflow-hidden"
          >
            <div className="absolute inset-0 opacity-5">
              <img 
                src="/images/overlay.jpg" 
                alt="Decorative frame" 
                className="absolute top-0 left-0 w-full h-full object-cover opacity-100"
              />
            </div>
            
            <div className="px-4 max-w-lg mx-auto">
              <h3 className="font-optima text-2xl text-center mb-8 text-[#846D4E]">Wedding Wishes</h3>
              <p className="font-optima text-md text-center mb-8 text-[#FEFEFE]/80">We'd be honored to have you share your wishes on our special day</p>
              
              {messages.length > 0 ? (
                <>
                  <div className="space-y-6 min-h-[300px]">
                    {messages
                      .slice(
                        currentMessagePage * messagesPerPage,
                        (currentMessagePage + 1) * messagesPerPage
                      )
                      .map((msg, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="text-center"
                        >
                          <p className="text-[#846D4E]/90 text-lg leading-relaxed">"{msg.message}"</p>
                          <p className="font-optima text-[#C27F89] text-base font-semibold capitalize">{msg.name}</p>
                        </motion.div>
                    ))}
                  </div>
                  
                  {/* Simplified Pagination Controls */}
                  {messages.length > messagesPerPage && (
                    <div className="flex justify-center items-center gap-6 mt-8">
                      <button
                        onClick={handlePrevMessages}
                        disabled={currentMessagePage === 0}
                        className="flex items-center justify-center px-6 py-2 bg-[#846D4E] text-white rounded-lg font-semibold text-lg shadow-lg hover:bg-[#705e42] active:bg-[#705e42] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105"
                      >
                        <span className="text-2xl">←</span>
                      </button>
                      <span className="text-[#846D4E] font-medium text-lg">
                        {currentMessagePage + 1} / {Math.ceil(messages.length / messagesPerPage)}
                      </span>
                      <button
                        onClick={handleNextMessages}
                        disabled={currentMessagePage >= Math.floor((messages.length - 1) / messagesPerPage)}
                        className="flex items-center justify-center px-6 py-2 bg-[#846D4E] text-white rounded-lg font-semibold text-lg shadow-lg hover:bg-[#705e42] active:bg-[#705e42] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105"
                      >
                        <span className="text-2xl">→</span>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center text-[#333]/70">
                  <p className="text-lg font-optima">No wishes yet.</p>
                </div>
              )}
            </div>
          </motion.section>

          {/* Countdown Section */}
          <motion.section 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={slideUp}
            className="py-16 bg-[#C27F89] text-[#FEFEFE] relative overflow-hidden"
          >
            {/* Decorative background pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-full h-full pattern-bg opacity-20"></div>
            </div>
            
            <h2 className="font-optima text-2xl text-center mb-8">COUNTDOWN</h2>
            <div className="grid grid-cols-4 gap-6 max-w-xs mx-auto px-4">
              <div className="text-center">
                <div className="font-optima text-3xl font-light mb-2">{timeLeft.days}</div>
                <div className="font-optima text-xs tracking-wider uppercase">Days</div>
              </div>
              <div className="text-center">
                <div className="font-optima text-3xl font-light mb-2">{timeLeft.hours}</div>
                <div className="font-optima text-xs tracking-wider uppercase">Hours</div>
              </div>
              <div className="text-center">
                <div className="font-optima text-3xl font-light mb-2">{timeLeft.minutes}</div>
                <div className="font-optima text-xs tracking-wider uppercase">Minutes</div>
              </div>
              <div className="text-center">
                <div className="font-optima text-3xl font-light mb-2">{timeLeft.seconds}</div>
                <div className="font-optima text-xs tracking-wider uppercase">Seconds</div>
              </div>
            </div>
          </motion.section>

          {/* Wishes & RSVP Section */}
          <section 
            ref={wishesSectionRef}
            className="py-16 bg-[#C27F89] relative overflow-hidden z-[1]"
          >
            <div className="px-4 relative z-[2]">
              <h3 className="font-optima text-2xl text-center text-[#FEFEFE]">Wedding Wishes</h3>
              <p className="font-optima text-md text-center mb-8 text-[#FEFEFE]/80">We'd be honored to have you share your wishes on our special day</p>
              
              {/* Tabs */}
              <div className="max-w-md mx-auto mb-12">
                <div className="relative flex rounded-full bg-[#FEFEFE]/20">
                  <motion.div
                    className="absolute h-full rounded-full bg-[#FEFEFE]"
                    initial={false}
                    animate={{ left: activeTab === 'rsvp' ? '0%' : '50%', width: '50%' }}
                    transition={{ type: "spring", damping: 30 }}
                  />
                  {['rsvp', 'wishes'].map((tab) => (
                    <button 
                      key={tab}
                      onClick={() => handleTabChange(tab)} 
                      className="relative flex-1 py-2.5 rounded-full text-sm font-optima z-10 transition-colors duration-300"
                      style={{ color: activeTab === tab ? '#C27F89' : '#FEFEFE' }}
                    >
                      {tab === 'rsvp' ? 'RSVP' : 'Send Wishes'}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Forms Container */}
              <div className="max-w-md mx-auto">
                {/* RSVP Form */}
                {activeTab === 'rsvp' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4 }}
                  >
                    <form onSubmit={handleSubmit} className="space-y-8">
                      <div className="space-y-8">
                        <div>
                          <input
                            type="text"
                            required
                            placeholder="Your Name"
                            className="w-full px-4 py-3 text-base bg-transparent border-b border-[#FEFEFE]/30 text-[#FEFEFE] focus:outline-none focus:border-[#EAD4B7] transition duration-200 placeholder-[#FEFEFE]"
                            value={wishesData.name}
                            onChange={(e) => setWishesData({...wishesData, name: e.target.value})}
                          />
                        </div>
                        
                        <div>
                          <input
                            type="tel"
                            required
                            placeholder="Phone Number"
                            className="w-full px-4 py-3 text-base bg-transparent border-b border-[#FEFEFE]/30 text-[#FEFEFE] focus:outline-none focus:border-[#EAD4B7] transition duration-200 placeholder-[#FEFEFE]"
                            value={wishesData.phone}
                            onChange={(e) => setWishesData({...wishesData, phone: e.target.value})}
                          />
                        </div>
                        
                        <div className="relative">
                          <select
                            required
                            className="w-full px-4 py-3 text-base bg-[#C27F89] border-b border-[#FEFEFE]/30 text-[#FEFEFE] focus:outline-none focus:border-[#EAD4B7] transition duration-200 appearance-none cursor-pointer placeholder-[#FEFEFE]"
                            value={wishesData.organization}
                            onChange={(e) => setWishesData({...wishesData, organization: e.target.value})}
                          >
                            <option value="" className="bg-[#C27F89] text-[#FEFEFE]/50">Organization/Group</option>
                            {ORGANIZATION_OPTIONS.map((option) => (
                              <option key={option} value={option} className="bg-[#C27F89]">
                                {option}
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 px-2 text-[#FEFEFE]">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                            </svg>
                          </div>
                        </div>
                        
                        <div className="relative">
                          <select
                            required
                            className="w-full px-4 py-3 text-base bg-[#C27F89] border-b border-[#FEFEFE]/30 text-[#FEFEFE] focus:outline-none focus:border-[#EAD4B7] transition duration-200 appearance-none cursor-pointer placeholder-[#FEFEFE]"
                            value={wishesData.invitationFrom}
                            onChange={(e) => setWishesData({...wishesData, invitationFrom: e.target.value})}
                          >
                            <option value="" className="bg-[#C27F89] text-[#FEFEFE]/50">Invitation From</option>
                            {INVITATION_FROM_OPTIONS.map((option) => (
                              <option key={option} value={option} className="bg-[#C27F89]">
                                {option}
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 px-2 text-[#FEFEFE]">
                            <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                            </svg>
                          </div>
                        </div>
                        
                        <div className="relative">
                          <select
                            required
                            className="w-full px-4 py-3 text-base bg-[#C27F89] border-b border-[#FEFEFE]/30 text-[#FEFEFE] focus:outline-none focus:border-[#EAD4B7] transition duration-200 appearance-none cursor-pointer placeholder-[#FEFEFE]"
                            value={wishesData.pax}
                            onChange={(e) => setWishesData({...wishesData, pax: parseInt(e.target.value) || 1})}
                          >
                            {[1, 2].map((num) => (
                              <option key={num} value={num} className="bg-[#C27F89]">
                                {num} pax
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 px-2 text-[#FEFEFE]">
                            <svg className="fill-current h-4 w-4 mb-8" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                              <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                            </svg>
                          </div>
                          <p className="mt-2 text-xs italic text-[#FEFEFE]/80">If you require more than 2 pax, you may refresh the link and enter the additional guest names.</p>
                        </div>
                        
                        <div>
                          <textarea
                            className="w-full px-4 py-3 text-base bg-transparent border-b border-[#FEFEFE]/30 text-[#FEFEFE] focus:outline-none focus:border-[#EAD4B7] transition duration-200 placeholder-[#FEFEFE]"
                            rows={3}
                            placeholder="Message (Optional)"
                            value={wishesData.message}
                            onChange={(e) => setWishesData({...wishesData, message: e.target.value})}
                          />
                        </div>
                      </div>
                      
                      {submitError && (
                        <p className="text-[#FFD2D2] text-sm text-center">{submitError}</p>
                      )}

                      {/* <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#C27F89] text-white font-bold py-4 rounded-full hover:bg-[#FFD2D2] hover:text-[#C27F89] transition text-lg shadow-md disabled:opacity-60 border-2 border-[#FFFFFF]"
                      >
                        {isSubmitting ? 'Confirming...' : 'Confirm RSVP'}
                      </button> */}
                      
                      <button
                        type="button"
                        onClick={() => alert('RSVP is now closed. Thank you for your interest!')}
                        className="w-full bg-[#C27F89] text-white font-bold py-4 rounded-full hover:bg-[#FFD2D2] hover:text-[#C27F89] transition text-lg shadow-md opacity-60 border-2 border-[#FFFFFF] cursor-not-allowed"
                      >
                        RSVP Closed
                      </button>
                    </form>
                  </motion.div>
                )}
                
                {/* Wishes Form */}
                {activeTab === 'wishes' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4 }}
                  >
                    <form onSubmit={handleWishesSubmit} className="space-y-8">
                      <div className="space-y-8">
                        <div>
                          <input
                            type="text"
                            required
                            placeholder="Your Name"
                            className="w-full px-4 py-3 text-base bg-transparent border-b border-[#FEFEFE]/30 text-[#FEFEFE] focus:outline-none focus:border-[#EAD4B7] transition duration-200 placeholder-[#FEFEFE]"
                            value={wishesData.name}
                            onChange={(e) => setWishesData({...wishesData, name: e.target.value})}
                          />
                        </div>
                        
                        <div>
                          <textarea
                            className="w-full px-4 py-3 text-base bg-transparent border-b border-[#FEFEFE]/30 text-[#FEFEFE] focus:outline-none focus:border-[#EAD4B7] transition duration-200 placeholder-[#FEFEFE]"
                            rows={5}
                            required
                            placeholder="Your Wishes"
                            value={wishesData.message}
                            onChange={(e) => setWishesData({...wishesData, message: e.target.value})}
                          />
                        </div>
                      </div>
                      
                      {submitError && (
                        <p className="text-[#FFD2D2] text-sm text-center">{submitError}</p>
                      )}
                      
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#C27F89] text-white font-bold py-4 rounded-full hover:bg-[#FFD2D2] hover:text-[#C27F89] transition text-lg shadow-md disabled:opacity-60 border-2 border-[#FFFFFF]"
                      >
                        {isSubmitting ? 'Sending...' : 'Send Wishes'}
                      </button>
                    </form>
                  </motion.div>
                )}
              </div>
            </div>
          </section>

          {/* Footer */}
          <motion.footer 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-[#846D4E] bg-[#FEFEFE] py-6 mb-16 space-y-2"
          >
            <p className="text-center text-sm opacity-75">Made with ❤️</p>
            <p className="text-center text-sm opacity-75 pb-5">© 2025 Strada. All rights reserved.</p>
          </motion.footer>
          
          {/* Quick Actions */}
          {showQuickActions && (
            <QuickActions 
              isPlaying={isPlaying}
              toggleMusic={toggleMusic}
              scrollToWishes={scrollToWishes}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default App; 